/**
 * Vault-PIN gate:
 *  - If no PIN is set (bootstrap), child content renders directly and a banner
 *    invites the admin to set up a PIN.
 *  - If PIN set but not unlocked, shows a PIN entry overlay.
 *  - Once unlocked, token is stored in sessionStorage under VAULT_PIN_KEY and
 *    automatically attached to subsequent vault fetches (via vaultFetch below).
 *  - Provides "Tresor sperren" control that clears the session + reloads gate.
 */
import { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { Shield, Lock, Unlock, KeyRound, X } from "lucide-react";
import { authFetch } from "../../App";

const API = "/api";
export const VAULT_PIN_KEY = "tgaone.vault.pinToken";

/** Wrap any fetch for a vault-protected endpoint — auto-attaches the unlock token. */
export async function vaultFetch(url, opts = {}) {
  const token = sessionStorage.getItem(VAULT_PIN_KEY) || "";
  const headers = { ...(opts.headers || {}) };
  if (token) headers["X-Vault-PIN-Token"] = token;
  return authFetch(url, { ...opts, headers });
}

export default function VaultPinGate({ children, onUnlocked }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSetup, setShowSetup] = useState(false);

  const refreshStatus = useCallback(async () => {
    try {
      const res = await vaultFetch(`${API}/vault/pin/status`);
      if (res.ok) setStatus(await res.json());
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { refreshStatus(); }, [refreshStatus]);

  const onPinSetOrUnlocked = async () => {
    await refreshStatus();
    onUnlocked?.();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24" style={{ color: "var(--tga-text-secondary)" }}>
        <div className="animate-spin h-6 w-6 border-2 border-current border-t-transparent rounded-full" />
      </div>
    );
  }

  const noPin = status && !status.pin_set;
  const lockedNow = status && status.pin_set && !status.unlocked;

  return (
    <>
      {/* Gate overlay when locked */}
      {lockedNow && <PinUnlockOverlay onUnlocked={onPinSetOrUnlocked} lockedUntil={status.locked_until} />}
      {/* Setup modal */}
      {showSetup && (
        <PinSetupModal
          hasExistingPin={status?.pin_set}
          onClose={() => setShowSetup(false)}
          onSaved={async () => { setShowSetup(false); await onPinSetOrUnlocked(); }}
        />
      )}
      {/* Content — always rendered when unlocked OR bootstrap (no PIN) */}
      {(status?.unlocked || noPin) && (
        <div>
          <div className="flex items-center justify-end gap-2 px-4 pt-3" data-testid="vault-pin-bar">
            {noPin ? (
              <button
                onClick={() => setShowSetup(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold bg-amber-500/15 text-amber-400 border border-amber-500/30 hover:bg-amber-500/25"
                data-testid="vault-pin-setup-btn"
              >
                <KeyRound className="w-3.5 h-3.5" /> PIN einrichten (empfohlen)
              </button>
            ) : (
              <>
                <button
                  onClick={() => setShowSetup(true)}
                  className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-semibold bg-white/5 hover:bg-white/10"
                  style={{ color: "var(--tga-text-secondary)" }}
                  data-testid="vault-pin-change-btn"
                >
                  <KeyRound className="w-3 h-3" /> PIN ändern
                </button>
                <button
                  onClick={async () => {
                    const token = sessionStorage.getItem(VAULT_PIN_KEY);
                    sessionStorage.removeItem(VAULT_PIN_KEY);
                    try {
                      await authFetch(`${API}/vault/pin/lock`, {
                        method: "POST",
                        body: JSON.stringify({ token }),
                      });
                    } catch { /* non-fatal */ }
                    toast.success("Tresor gesperrt");
                    await refreshStatus();
                  }}
                  className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-semibold bg-red-500/10 text-red-400 hover:bg-red-500/20 border border-red-500/20"
                  data-testid="vault-pin-lock-btn"
                >
                  <Lock className="w-3 h-3" /> Tresor sperren
                </button>
              </>
            )}
          </div>
          {children}
        </div>
      )}
    </>
  );
}

function PinUnlockOverlay({ onUnlocked, lockedUntil }) {
  const [pin, setPin] = useState("");
  const [working, setWorking] = useState(false);
  const [err, setErr] = useState("");

  const locked = lockedUntil && new Date(lockedUntil) > new Date();

  const submit = async () => {
    if (working || !pin) return;
    setWorking(true); setErr("");
    try {
      const res = await authFetch(`${API}/vault/pin/unlock`, {
        method: "POST",
        body: JSON.stringify({ pin }),
      });
      const d = await res.json().catch(() => ({}));
      if (res.ok && d.token) {
        sessionStorage.setItem(VAULT_PIN_KEY, d.token);
        toast.success("Tresor entsperrt");
        setPin("");
        onUnlocked();
      } else {
        setErr(d.detail || "PIN falsch");
      }
    } catch {
      setErr("Netzwerkfehler");
    }
    setWorking(false);
  };

  return createPortal(
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[100] p-6" data-testid="vault-pin-overlay">
      <div className="max-w-sm w-full rounded-2xl border p-6 space-y-4" style={{ backgroundColor: "#0f1117", borderColor: "var(--tga-border)" }}>
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-full bg-amber-500/15 flex items-center justify-center">
            <Shield className="w-6 h-6 text-amber-400" />
          </div>
          <div>
            <h2 className="text-base font-bold" style={{ color: "var(--tga-text)" }}>Tresor gesperrt</h2>
            <p className="text-[11px]" style={{ color: "var(--tga-text-secondary)" }}>PIN eingeben, um Passwörter zu sehen</p>
          </div>
        </div>
        {locked ? (
          <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-xs text-red-400">
            PIN gesperrt wegen zu vieler Fehlversuche. Entsperrt wieder um {new Date(lockedUntil).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })}.
          </div>
        ) : (
          <>
            <input
              type="password"
              inputMode="numeric"
              autoComplete="off"
              pattern="[0-9]*"
              maxLength={8}
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
              onKeyDown={(e) => e.key === "Enter" && submit()}
              placeholder="• • • •"
              autoFocus
              className="w-full px-4 py-3 text-center text-2xl font-mono tracking-[0.5em] rounded-xl outline-none border"
              style={{ backgroundColor: "rgba(0,0,0,0.3)", color: "var(--tga-text)", borderColor: "var(--tga-border)" }}
              data-testid="vault-pin-input"
            />
            {err && <p className="text-xs text-red-400" data-testid="vault-pin-err">{err}</p>}
            <button
              onClick={submit}
              disabled={working || !pin}
              className="w-full px-4 py-2.5 rounded-xl text-sm font-bold bg-amber-500 hover:bg-amber-400 text-black disabled:opacity-40"
              data-testid="vault-pin-submit"
            >
              <Unlock className="w-4 h-4 inline mr-1.5" /> Entsperren
            </button>
          </>
        )}
      </div>
    </div>,
    document.body
  );
}

function PinSetupModal({ hasExistingPin, onClose, onSaved }) {
  const [currentPin, setCurrentPin] = useState("");
  const [newPin, setNewPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [working, setWorking] = useState(false);

  const submit = async () => {
    if (newPin.length < 4) { toast.error("PIN muss mindestens 4 Ziffern haben"); return; }
    if (newPin !== confirmPin) { toast.error("PIN-Bestätigung stimmt nicht überein"); return; }
    if (hasExistingPin && !currentPin) { toast.error("Aktuelle PIN erforderlich"); return; }
    setWorking(true);
    try {
      const res = await authFetch(`${API}/vault/pin/setup`, {
        method: "POST",
        body: JSON.stringify({ current_pin: currentPin, new_pin: newPin, confirm_pin: confirmPin }),
      });
      const d = await res.json().catch(() => ({}));
      if (res.ok) {
        toast.success(hasExistingPin ? "PIN geändert" : "PIN eingerichtet");
        sessionStorage.removeItem(VAULT_PIN_KEY);  // force re-unlock with new PIN
        onSaved();
      } else {
        toast.error(d.detail || "Fehler beim Setzen der PIN");
      }
    } catch { toast.error("Netzwerkfehler"); }
    setWorking(false);
  };

  return createPortal(
    <div className="fixed inset-0 bg-black/75 backdrop-blur-sm flex items-center justify-center z-[100] p-6" data-testid="vault-pin-setup-modal">
      <div className="max-w-sm w-full rounded-2xl border p-5 space-y-3" style={{ backgroundColor: "#0f1117", borderColor: "var(--tga-border)" }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <KeyRound className="w-5 h-5 text-amber-400" />
            <h2 className="text-sm font-bold" style={{ color: "var(--tga-text)" }}>{hasExistingPin ? "PIN ändern" : "Vault-PIN einrichten"}</h2>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-white/10">
            <X className="w-4 h-4" style={{ color: "var(--tga-text-secondary)" }} />
          </button>
        </div>
        <p className="text-[11px]" style={{ color: "var(--tga-text-secondary)" }}>
          Die PIN wird vom Login-Passwort getrennt gespeichert. Sie schützt den Zugriff auf gespeicherte Passwörter, auch wenn Deine Session missbraucht wird.
        </p>
        {hasExistingPin && (
          <PinField label="Aktuelle PIN" value={currentPin} onChange={setCurrentPin} testId="vault-pin-current" />
        )}
        <PinField label="Neue PIN (4-8 Ziffern)" value={newPin} onChange={setNewPin} testId="vault-pin-new" />
        <PinField label="PIN bestätigen" value={confirmPin} onChange={setConfirmPin} testId="vault-pin-confirm" />
        <button
          onClick={submit}
          disabled={working}
          className="w-full px-4 py-2.5 rounded-xl text-sm font-bold bg-amber-500 hover:bg-amber-400 text-black disabled:opacity-40"
          data-testid="vault-pin-save"
        >
          Speichern
        </button>
      </div>
    </div>,
    document.body
  );
}

function PinField({ label, value, onChange, testId }) {
  return (
    <div>
      <label className="text-[10px] font-bold uppercase opacity-70" style={{ color: "var(--tga-text-secondary)" }}>{label}</label>
      <input
        type="password"
        inputMode="numeric"
        autoComplete="off"
        pattern="[0-9]*"
        maxLength={8}
        value={value}
        onChange={(e) => onChange(e.target.value.replace(/\D/g, ""))}
        className="w-full mt-0.5 px-3 py-2 text-center text-lg font-mono tracking-[0.3em] rounded-lg outline-none border"
        style={{ backgroundColor: "rgba(0,0,0,0.3)", color: "var(--tga-text)", borderColor: "var(--tga-border)" }}
        data-testid={testId}
      />
    </div>
  );
}
